import React, { useState } from 'react';
import { LogOut, Trash2, AlertCircle, CheckCircle } from 'lucide-react';

/**
 * Compliance Footer Component
 * Shows privacy policy, data deletion, and disconnect options
 */
function ComplianceFooter({ onDisconnect }) {
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectStep, setDisconnectStep] = useState(0);

  const handleDisconnect = async () => {
    if (disconnectStep === 0) {
      // Show warning
      setDisconnectStep(1);
      return;
    }

    if (disconnectStep === 1) {
      // Perform disconnect
      setDisconnecting(true);
      try {
        // Clear session data
        localStorage.removeItem('repugate_session');
        sessionStorage.removeItem('repugate_session');

        // Log activity
        await fetch(
          `${process.env.REACT_APP_API_URL}/providers/log-activity`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: 'facebook_disconnected',
              status: 'success'
            })
          }
        ).catch(() => {}); // Non-fatal if logging fails

        setDisconnectStep(2); // Show success
        setTimeout(() => {
          setShowDisconnectModal(false);
          onDisconnect();
        }, 2000);
      } catch (err) {
        console.error('Disconnect error:', err);
        setDisconnecting(false);
        setDisconnectStep(0);
      }
    }
  };

  return (
    <>
      {/* Footer */}
      <footer className="bg-slate-900/50 border-t border-purple-500/10 mt-12 py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* About */}
            <div>
              <h3 className="text-sm font-semibold text-white mb-3">RepuGate</h3>
              <p className="text-xs text-purple-300/70">
                AI-powered Facebook review management. Respond to reviews faster, smarter, in Bangla or English.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-sm font-semibold text-white mb-3">Resources</h3>
              <ul className="space-y-2 text-xs">
                <li>
                  <a
                    href="https://repugate.vercel.app/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-400 hover:text-purple-300 transition"
                  >
                    📋 Privacy Policy
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:support@repugate.app"
                    className="text-purple-400 hover:text-purple-300 transition"
                  >
                    📧 Contact Support
                  </a>
                </li>
              </ul>
            </div>

            {/* Account Management */}
            <div>
              <h3 className="text-sm font-semibold text-white mb-3">Account</h3>
              <button
                onClick={() => setShowDisconnectModal(true)}
                className="text-xs text-red-400 hover:text-red-300 transition flex items-center gap-2"
              >
                <LogOut className="w-3 h-3" />
                Disconnect Facebook
              </button>
            </div>
          </div>

          {/* Trust Message */}
          <div className="border-t border-slate-700 pt-6 flex items-start gap-3 text-xs text-purple-300/70">
            <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-purple-200">Your data is secure</p>
              <p>
                ✓ No automatic posting without your action
                <br/>
                ✓ Facebook tokens secured and never stored
                <br/>
                ✓ You can disconnect and delete all data anytime
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Disconnect Modal */}
      {showDisconnectModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-red-500/30 rounded-2xl shadow-2xl max-w-md w-full p-8">
            {disconnectStep === 0 && (
              <>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <AlertCircle className="text-yellow-400" size={28} />
                  Disconnect?
                </h2>
                <p className="text-purple-200 mb-6">
                  This will sign you out and optionally delete your data. You can reconnect anytime.
                </p>

                <div className="space-y-3">
                  <button
                    onClick={() => setShowDisconnectModal(false)}
                    className="w-full py-2.5 bg-slate-700/50 text-purple-200 border border-slate-600 rounded-lg hover:bg-slate-700 transition"
                  >
                    Keep Connected
                  </button>
                  <button
                    onClick={handleDisconnect}
                    className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition flex items-center justify-center gap-2"
                  >
                    <LogOut size={16} />
                    Disconnect
                  </button>
                </div>
              </>
            )}

            {disconnectStep === 1 && (
              <>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <Trash2 className="text-red-400" size={28} />
                  Confirm Disconnection
                </h2>
                <p className="text-purple-200 mb-6">
                  Are you sure? This will:
                </p>
                <ul className="space-y-2 text-sm text-purple-300 mb-6">
                  <li>✓ Sign you out immediately</li>
                  <li>✓ Clear your session data</li>
                  <li>✓ You can log back in anytime</li>
                </ul>

                <div className="space-y-3">
                  <button
                    onClick={() => setDisconnectStep(0)}
                    className="w-full py-2.5 bg-slate-700/50 text-purple-200 border border-slate-600 rounded-lg hover:bg-slate-700 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                    className={`w-full py-2.5 rounded-lg font-semibold transition flex items-center justify-center gap-2 ${
                      disconnecting
                        ? 'bg-red-600/50 text-red-200 cursor-not-allowed'
                        : 'bg-red-600 hover:bg-red-700 text-white'
                    }`}
                  >
                    {disconnecting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-transparent border-t-white" />
                        Disconnecting...
                      </>
                    ) : (
                      <>
                        <LogOut size={16} />
                        Permanently Disconnect
                      </>
                    )}
                  </button>
                </div>
              </>
            )}

            {disconnectStep === 2 && (
              <>
                <div className="text-center">
                  <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-white mb-2">Disconnected</h2>
                  <p className="text-purple-200 mb-6">
                    You've been successfully disconnected from Facebook. Redirecting to login...
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default ComplianceFooter;
